import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { isCapacitorNativeSync } from '../native/bridge';
import { runOtaUpdate, type OtaProgress, type OtaResult } from '../native/ota';
import type { BootstrapProgress } from '../sync/engine';
import { ProgressSetupOverlay } from './ProgressSetupOverlay';

// ─── Phase 0: Over-the-Air-Update (Capgo, MANUAL/SELF-HOSTED) ────────────────
// Der OtaProvider laeuft GANZ OBEN — vor SyncProvider/ContentSetupOverlay —
// damit ein neues Web-Bundle eingespielt werden kann, BEVOR der Daten/Asset-
// Bootstrap startet. Ablauf (native only):
//   1. runOtaUpdate(onProgress) → notifyAppReady() + /api/ota/check
//   2. Falls Update verfuegbar: download() mit Fortschritts-Listener →
//      Overlay zeigt den Balken; danach set()+reload() (kehrt NICHT zurueck).
//   3. Sonst (oder Web/Fehler): {updated:false} → Kinder sofort rendern.
// Es gibt KEINEN „Spaeter"/Skip-Button. Ein Download-/Netzfehler darf den User
// aber NICHT aussperren: runOtaUpdate faengt alles ab und liefert {updated:false},
// wir laufen dann auf dem AKTUELLEN Bundle weiter (Retry beim naechsten Start).

interface OtaContextValue {
  // Nur waehrend eines aktiven Bundle-Downloads befuellt; sonst null.
  otaProgress: BootstrapProgress | null;
}

const OtaContext = createContext<OtaContextValue>({ otaProgress: null });

// Wandelt den schlanken OTA-Fortschritt in die BootstrapProgress-Form, damit
// das gemeinsame ProgressSetupOverlay denselben Balken rendern kann.
function toBootstrapProgress(p: OtaProgress): BootstrapProgress {
  const percentage = Math.max(0, Math.min(100, Math.round(p.percent ?? 0)));
  const bytesDone = typeof p.bytesDone === 'number' && Number.isFinite(p.bytesDone) ? p.bytesDone : 0;
  const bytesTotal = typeof p.bytesTotal === 'number' && Number.isFinite(p.bytesTotal) ? p.bytesTotal : 0;
  return {
    phase: 'bundle',
    percentage,
    dataStep: 0,
    dataSteps: 0,
    assetsDone: 0,
    assetsTotal: 0,
    bytesDone,
    bytesTotal,
    etaSeconds: null,
    waitingForNetwork: false,
  };
}

export function OtaProvider({ children }: { children: ReactNode }) {
  // Auf Web/PWA (kein Capacitor) ist OTA ein No-Op → sofort fertig. Der
  // synchrone Check vermeidet ein Aufblitzen des Overlays im Web.
  const native = isCapacitorNativeSync();
  // Beim reinen Check darf noch keine Aktualisierung angekuendigt werden. Erst
  // das Bundle-Signal aus runOtaUpdate beweist, dass decideOtaApply wirklich ein
  // Update gewaehlt hat. reload() zerstoert bei Erfolg den Kontext ohnehin,
  // bevor 'done' je sichtbar wuerde.
  const [phase, setPhase] = useState<'checking' | 'updating' | 'done'>(() => (
    native ? 'checking' : 'done'
  ));
  const [otaProgress, setOtaProgress] = useState<BootstrapProgress | null>(null);
  // Der Promise-Ref ueberlebt React.StrictModes Setup→Cleanup→Setup-Replay.
  // So laeuft der native OTA-Check genau einmal, waehrend der zweite Effekt
  // trotzdem den laufenden Promise und seinen Abschluss weiter beobachtet.
  const runPromiseRef = useRef<Promise<OtaResult> | null>(null);
  const mountedRef = useRef(false);
  const releasedBySafetyRef = useRef(false);
  const latestProgressRef = useRef<OtaProgress | null>(null);
  const effectRevisionRef = useRef(0);

  useEffect(() => {
    if (!native) return undefined;
    const effectRevision = ++effectRevisionRef.current;
    mountedRef.current = true;
    // Nach dem Backstop bleibt das Gate offen. Der nicht abbrechbare native
    // Lauf darf spaeter fertig werden, aber weder Fortschritt noch finally
    // duerfen das Overlay dann erneut vor die bereits gerenderten Kinder legen.

    // HARTER Gesamt-Backstop: egal was passiert, der Nutzer wird NIE laenger als
    // dies im no-skip-Overlay festgehalten. Grosszuegig (> getInfo 6s + check 12s
    // + download-Timeout 120s), damit ein legitimer langsamer Download nicht
    // abgeschnitten wird; greift nur bei einem echten Haenger.
    const safety = window.setTimeout(() => {
      if (mountedRef.current && effectRevisionRef.current === effectRevision) {
        console.warn('[OTA] Gesamt-Timeout (150s) erreicht — weiter in die App auf dem aktuellen Bundle.');
        releasedBySafetyRef.current = true;
        setOtaProgress(null);
        setPhase('done');
      }
    }, 150000);

    if (!runPromiseRef.current) {
      runPromiseRef.current = runOtaUpdate((p: OtaProgress) => {
        latestProgressRef.current = p;
        if (!mountedRef.current || releasedBySafetyRef.current) return;
        // Dieses Signal kommt erst nach der positiven OTA-Entscheidung. Ab
        // hier ist die Aktualisierungs-Wortwahl wahrheitsgemaess.
        if (p.phase === 'bundle') {
          setOtaProgress(toBootstrapProgress(p));
          setPhase('updating');
        }
      }).catch((error): OtaResult => {
        // Sicherheitsnetz: runOtaUpdate soll NIE werfen — falls doch, nicht
        // bricken, sondern in die App durchlassen.
        console.warn('[OTA] runOtaUpdate threw unexpectedly; proceeding on current bundle.', error);
        return { updated: false, error };
      });
    }

    // Ein Fortschritt kann theoretisch genau zwischen StrictMode-Cleanup und
    // zweitem Setup eintreffen. Den letzten Stand dann sofort nachziehen.
    const latestProgress = latestProgressRef.current;
    if (!releasedBySafetyRef.current && latestProgress?.phase === 'bundle') {
      setOtaProgress(toBootstrapProgress(latestProgress));
      setPhase('updating');
    }

    void runPromiseRef.current.finally(() => {
      if (
        mountedRef.current
        && effectRevisionRef.current === effectRevision
        && !releasedBySafetyRef.current
      ) {
        window.clearTimeout(safety);
        setOtaProgress(null);
        setPhase('done');
      }
    });

    return () => {
      mountedRef.current = false;
      window.clearTimeout(safety);
    };
  }, [native]);

  const value = useMemo<OtaContextValue>(() => ({ otaProgress }), [otaProgress]);

  // Waehrend des Checks bleibt die Flaeche bewusst wortlos: Zu diesem Zeitpunkt
  // ist noch kein Update bekannt. Kinder mounten trotzdem erst nach der
  // Entscheidung; der 6%-Floor-Schimmer signalisiert lediglich App-Start.
  if (phase === 'checking') {
    return (
      <OtaContext.Provider value={value}>
        <ProgressSetupOverlay
          progress={null}
          showTips={false}
          silent
        />
      </OtaContext.Provider>
    );
  }

  if (phase === 'updating') {
    return (
      <OtaContext.Provider value={value}>
        <ProgressSetupOverlay
          progress={otaProgress}
          titleKey="app.ota_bundle_title"
          hintKey="app.ota_downloading"
          showTips={false}
        />
      </OtaContext.Provider>
    );
  }

  return <OtaContext.Provider value={value}>{children}</OtaContext.Provider>;
}

export function useOta(): OtaContextValue {
  return useContext(OtaContext);
}
